import {
  Component,
  computed,
  effect,
  HostBinding,
  Signal,
  signal,
} from '@angular/core';
import { IconsComponent } from '@shared/components/icons/icon.component';
import {
  bootstrapCheck2All,
  bootstrapClipboard2Check,
  bootstrapExclamationOctagonFill,
  bootstrapShieldFillCheck,
  bootstrapShieldFillX,
  bootstrapXCircle,
} from '@ng-icons/bootstrap-icons';
import {
  CustomBiometricCaptureFaceComponent,
  CustomBiometricCaptureFingerPrintComponent,
  CustomBiometricValidationMethodSelectionComponent,
  CustomButtonComponent,
  CustomDropdownComponent,
  CustomModalComponent,
  onBiometricFaceCapturedEvent,
  onBiometricFingerprintCapturedEvent,
  VUOnboardingFaceComponent,
} from '@shared/components';
import {
  biometricValidationMethod,
  buttonConfig,
  dropdownConfig,
  modalConfig,
  ResponseError,
} from '@core/types/types';
import { TranslateModule } from '@ngx-translate/core';
import {
  ModalService,
  RouteWatcherService,
  ToastService,
} from '@core/services';
import { ClaimService } from '@core/services/http/claim.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { pageIds } from '@core/enums/enums';
import { PreauthorizedClaimCreatedModalContentComponent } from './components/preauthorized-claim-created-modal-content/preauthorized-claim-created-modal-content.component';

type modalContent = {
  id: string;
  current: boolean;
  title: string;
  size: modalConfig['size'];
  content: any;
  data: any;
  previousComponent?: string;
  nextComponent?: string;
  actions?: any;
};

@Component({
  selector: 'create-claim-button-component',
  templateUrl: 'create-claim-button.component.html',
  imports: [CustomButtonComponent, CustomDropdownComponent, TranslateModule],
})
export class CreateClaimButtonComponent {
  @HostBinding('style.display') display = '';

  private claim$?: Subscription;

  protected allowPreauthorization = false;

  private readonly modalRefs = {
    biometric: {
      validationMethodSelection: 'biometricValidationMethodSelection',
      captureFingerprint: 'captureFingerprint',
      captureFace: 'captureFace',
      claimCreationProgress: 'claimCreationProgress',
      preauthorizationCreationProgress: 'preauthorizationCreationProgress',
      preauthorizedClaimCreated: 'preauthorizedClaimCreated',
    },
  };

  private readonly signals = {
    selectedBiometricValidationMethod: signal<
      biometricValidationMethod | undefined
    >(undefined),
    currentBiometricModal: signal<string>(
      this.modalRefs.biometric.validationMethodSelection
    ),
    fingerprintCapture: signal<onBiometricFingerprintCapturedEvent | undefined>(
      undefined
    ),
    faceCapture: signal<onBiometricFaceCapturedEvent | undefined>(undefined),
  };

  protected blockClaimCreationProgressModalDismiss = true;

  protected configs = computed(() => ({
    btn: {
      type: 'button',
      label: {
        key: 'app.pageId.generate-claim',
      },
      startContent: {
        content: IconsComponent,
        data: {
          icon: bootstrapCheck2All,
        },
      },
      animation: 'slide-in-right',
      action: this.openModal.bind(this),
    } as buttonConfig,
    dropdown: {
      type: 'dropdown',
      dropdownContainer: null,
      customBtn: {
        type: 'button',
        label: {
          key: 'app.pageId.generate',
        },
        startContent: {
          content: IconsComponent,
          data: {
            icon: bootstrapCheck2All,
          },
        },
        animation: 'slide-in-right',
      } as buttonConfig,
      actions: [
        {
          name: 'app.pageId.pre-authorization',
          label: 'app.pageId.pre-authorization',
          icon: bootstrapClipboard2Check,
          action: this.createPreauthorizedClaim.bind(this),
        },
        {
          name: 'app.pageId.authorization',
          label: 'app.pageId.authorization',
          icon: bootstrapCheck2All,
          action: this.openModal.bind(this),
        },
      ],
    } as dropdownConfig,
  }));

  constructor(
    private readonly routeWatcher: RouteWatcherService,
    private readonly modalService: ModalService,
    private readonly claimService: ClaimService,
    private readonly router: Router,
    private readonly toastService: ToastService
  ) {
    effect(() => {
      const watchedRoutes = this.routeWatcher.getWatchedRoutes([
        'new-claim',
        'continue-process-claim',
      ]);

      if (watchedRoutes.includes('new-claim')) {
        // Preautorización oculta temporalmente: pendiente el endpoint externo
        // para integrar correctamente con las ARS. Para reactivarla, restaurar
        // la condición previa (sponsorSlug == sponsorSlugs.UNIVERSAL) y volver
        // a importar sponsorSlugs desde @core/enums/enums.
        this.allowPreauthorization = false;
      }

      this.display =
        watchedRoutes.includes('new-claim') ||
        watchedRoutes.includes('continue-process-claim')
          ? ''
          : 'none';
    });
  }

  private readonly modalContents: Signal<modalContent[]> = computed(() => [
    {
      id: this.modalRefs.biometric.validationMethodSelection,
      current:
        this.signals.currentBiometricModal() ==
        this.modalRefs.biometric.validationMethodSelection,
      title: 'app.pageId.biometric.validation-method-selection.title',
      size: 'md',
      content: CustomBiometricValidationMethodSelectionComponent,
      data: {
        listeners: [
          {
            event: 'onBiometricValidationMethodSelected',
            action: this.onBiometricValidationMethodSelected.bind(this),
          },
        ],
      },
      previousComponent: undefined,
      nextComponent:
        this.signals.selectedBiometricValidationMethod() == 'fingerprint'
          ? this.modalRefs.biometric.captureFingerprint
          : this.signals.selectedBiometricValidationMethod() == 'face'
          ? this.modalRefs.biometric.captureFace
          : undefined,
      actions: computed(() => [
        {
          type: 'button',
          label: {
            key: 'common.cancel',
          },
          color: 'tertiary',
          theme: 'light',
          action: () => {
            this.modalService.dismiss(
              this.modalRefs.biometric.validationMethodSelection
            );
          },
        },
        {
          type: 'button',
          label: {
            key: 'common.continue',
          },
          disabled:
            this.signals.selectedBiometricValidationMethod() == undefined,
          color: 'primary',
          action: this.goToNextModal.bind(this),
        },
      ]),
    },
    {
      id: this.modalRefs.biometric.captureFingerprint,
      current:
        this.signals.currentBiometricModal() ==
        this.modalRefs.biometric.captureFingerprint,
      title: 'app.pageId.biometric.capture-fingerprint.title',
      size: 'lg',
      content: CustomBiometricCaptureFingerPrintComponent,
      data: {
        listeners: [
          {
            event: 'onBiometricFingerprintValidated',
            action: this.onBiometricFingerprintValidated.bind(this),
          },
        ],
      },
      previousComponent: this.modalRefs.biometric.validationMethodSelection,
      nextComponent: undefined,
      actions: computed(() => [
        {
          type: 'button',
          label: {
            key: 'common.cancel',
          },
          color: 'tertiary',
          theme: 'light',
          action: () => {
            this.modalService.dismiss(
              this.modalRefs.biometric.captureFingerprint
            );
          },
        },
        {
          type: 'button',
          startContent: {
            content: IconsComponent,
            data: {
              icon: bootstrapCheck2All,
              color: 'white',
            },
          },
          label: {
            key: 'common.authorize',
          },
          disabled: this.signals.fingerprintCapture() == undefined,
          color: 'primary',
          action: this.createClaim.bind(this),
        },
      ]),
    },
    {
      id: this.modalRefs.biometric.captureFace,
      current:
        this.signals.currentBiometricModal() ==
        this.modalRefs.biometric.captureFace,
      title: 'app.pageId.biometric.capture-face.title',
      size: 'lg',
      content: VUOnboardingFaceComponent,
      data: {
        listeners: [
          {
            event: 'onBiometricFaceValidated',
            action: this.onBiometricFaceValidated.bind(this),
          },
        ],
      },
      previousComponent: this.modalRefs.biometric.validationMethodSelection,
      nextComponent: undefined,
      actions: computed(() => [
        {
          type: 'button',
          label: {
            key: 'common.cancel',
          },
          color: 'tertiary',
          theme: 'light',
          action: () => {
            this.modalService.dismiss(this.modalRefs.biometric.captureFace);
          },
        },
        {
          type: 'button',
          startContent: {
            content: IconsComponent,
            data: {
              icon: bootstrapCheck2All,
              color: 'white',
            },
          },
          label: {
            key: 'common.authorize',
          },
          disabled: this.signals.faceCapture() == undefined,
          color: 'primary',
          action: this.createClaim.bind(this),
        },
      ]),
    },
  ]);

  private readonly currentModalContent = computed(() =>
    this.modalContents().find(
      (modal) => modal.id === this.signals.currentBiometricModal()
    )
  );

  openModal() {
    if (
      !this.claimService.createClaimInfo().dataClaim?.doctorName &&
      !this.claimService.createClaimInfo().dataClaim
        ?.doctorMedicalLicenseNumber &&
      !this.claimService.createClaimInfo().dataClaim?.doctorSpecialityName
    )
      return this.toastService.show({
        ref: 'doctorInformationRequiredError',
        color: 'secondary',
        delay: 3000,
        message: {
          key: 'app.pageId.claim.no-prescriptor-doctor-selected',
        },
        icon: bootstrapExclamationOctagonFill,
      });

    if (
      this.claimService.initialClaimInfo()?.biometricVerificationRequired ===
      false
    )
      return this.createClaim();

    const current = this.currentModalContent();
    if (!current) return;

    const modalConfig = () => ({
      type: 'custom',
      beforeDismiss: this.beforeModalDismiss.bind(this),
      title: current.title,
      size: current.size,
      body: {
        content: current.content,
        data: current.data,
      },
      actions: current.actions(),
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      current.id
    );
  }

  openProgressModal() {
    const modalConfig = () => ({
      type: 'progress',
      beforeDismiss: this.beforeClaimCreationProgressModalDismiss.bind(this),
      title: `app.pageId.claim.creating-${
        this.claimService.initialClaimInfo()?.claimInfo.type
      }`,
      message: 'app.pageId.generating-claim',
      size: 'sm',
      loader: {
        type: 'spinner',
        color: 'secondary',
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.biometric.claimCreationProgress
    );
  }

  openPreauthorizationProgressModal() {
    const modalConfig = () => ({
      type: 'progress',
      beforeDismiss: this.beforeClaimCreationProgressModalDismiss.bind(this),
      title: `app.pageId.claim.creating-${
        this.claimService.initialClaimInfo()?.claimInfo.type
      }-preauthorization`,
      message: 'app.pageId.generating-preauthorization',
      size: 'sm',
      loader: {
        type: 'spinner',
        color: 'secondary',
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.biometric.preauthorizationCreationProgress
    );
  }

  beforeClaimCreationProgressModalDismiss() {
    return !this.blockClaimCreationProgressModalDismiss;
  }

  openPreauthorizedClaimCreatedModal() {
    const modalConfig = () => ({
      type: 'custom',
      beforeDismiss: () => false,
      // title: `app.pageId.claim.${currentSection}-preauthorization-creation`,
      size: 'md',
      body: {
        content: PreauthorizedClaimCreatedModalContentComponent,
        data: undefined,
      },
      // actions: current.actions(),
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.biometric.preauthorizedClaimCreated
    );
  }

  goToNextModal() {
    const next = this.currentModalContent()?.nextComponent;
    const current = this.currentModalContent()?.id;

    if (current) {
      this.modalService.dismiss(current);
    }

    if (next) {
      this.signals.currentBiometricModal.set(next);
      this.openModal();
    }
  }

  onBiometricValidationMethodSelected(data: {
    method: biometricValidationMethod;
  }) {
    this.signals.selectedBiometricValidationMethod.set(data.method);
  }

  onBiometricFaceValidated(data: onBiometricFaceCapturedEvent) {
    this.claimService.updateCreateClaimInfo({
      biometricReadingId: data.biometricValidationId,
      biometricType: 'FACE_RECOGNITION',
      biometricValue: data.faceCapture,
    });

    this.signals.faceCapture.set(data);
  }

  onBiometricFingerprintValidated(data: onBiometricFingerprintCapturedEvent) {
    this.claimService.updateCreateClaimInfo({
      biometricReadingId: data.biometricValidationId,
      biometricType: 'FINGERPRINT_READING',
      biometricValue: data.fingerprintCapture,
    });

    this.signals.fingerprintCapture.set(data);
  }

  beforeModalDismiss() {
    if (
      this.signals.currentBiometricModal() ==
      this.modalRefs.biometric.validationMethodSelection
    ) {
      this.signals.selectedBiometricValidationMethod.set(undefined);
    }

    if (
      this.signals
        .currentBiometricModal()
        .includes(this.modalRefs.biometric.captureFace) ||
      this.signals
        .currentBiometricModal()
        .includes(this.modalRefs.biometric.captureFingerprint)
    ) {
      this.signals.selectedBiometricValidationMethod.set(undefined);
      this.signals.currentBiometricModal.set(
        this.modalRefs.biometric.validationMethodSelection
      );
      this.signals.fingerprintCapture.set(undefined);
      this.signals.faceCapture.set(undefined);
    }

    return true;
  }

  createClaim() {
    const currentSection = this.routeWatcher.getCurrentSection([
      pageIds.MEDICAL_ENCOUNTER,
      pageIds.MEDICAL_PROCEDURE,
      pageIds.LABORATORY,
      pageIds.CONTINUE_PROCESS_CLAIM,
    ]);

    const currentBiometricModal = this.currentModalContent();
    if (currentBiometricModal)
      this.modalService.dismiss(currentBiometricModal.id);

    this.openProgressModal();

    this.claim$ = (
      currentSection == pageIds.CONTINUE_PROCESS_CLAIM
        ? this.claimService.completePreauthorizedClaim(
            this.routeWatcher.getRouteParam(3)!,
            this.claimService.initialClaimInfo()!.token!
          )
        : this.claimService.createClaim(
            this.claimService.initialClaimInfo()!.token!
          )
    ).subscribe({
      next: (response) => {
        if (!response.data) {
          this.blockClaimCreationProgressModalDismiss = false;

          this.modalService.dismiss(
            this.modalRefs.biometric.claimCreationProgress
          );

          this.toastService.show({
            ref: 'claimCreatedError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: {
                message: (response as any as ResponseError).error.message,
                code: (response as any as ResponseError).error.error,
              },
            },
            icon: bootstrapShieldFillX,
          });

          throw response;
        }

        this.loadClaimInfo(response.data.solicitudReclamoId);
      },
      error: (error: ResponseError) => {
        this.blockClaimCreationProgressModalDismiss = false;
        this.modalService.dismiss(
          this.modalRefs.biometric.claimCreationProgress
        );

        this.toastService.show({
          ref: 'claimCreated',
          color: 'tertiary',
          message: {
            key: 'error.generic',
            params: { message: error.error.message, code: error.error.error },
          },
          icon: bootstrapXCircle,
        });
      },
    });
  }

  createPreauthorizedClaim() {
    this.openPreauthorizationProgressModal();

    this.claim$ = this.claimService
      .createPreauthorizedClaim(this.claimService.initialClaimInfo()!.token!)
      .subscribe({
        next: (response) => {
          if (!response.data) {
            this.blockClaimCreationProgressModalDismiss = false;

            this.modalService.dismiss(
              this.modalRefs.biometric.preauthorizationCreationProgress
            );

            this.toastService.show({
              ref: 'preauthorizationCreatedError',
              color: 'tertiary',
              message: {
                key: 'error.generic',
                params: {
                  message: (response as any as ResponseError).error.message,
                  code: (response as any as ResponseError).error.error,
                },
              },
              icon: bootstrapShieldFillX,
            });

            throw response;
          }

          this.loadPreAuthorizedClaimInfo(response.data.solicitudReclamoId);
        },
        error: (error: ResponseError) => {
          this.blockClaimCreationProgressModalDismiss = false;
          this.modalService.dismiss(
            this.modalRefs.biometric.preauthorizationCreationProgress
          );

          this.toastService.show({
            ref: 'preauthorizationCreatedError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: { message: error.error.message, code: error.error.error },
            },
            icon: bootstrapXCircle,
          });
        },
      });
  }

  loadClaimInfo(solicitudReclamoId: number) {
    this.claimService
      .getCreatedClaimInfo(
        this.claimService.initialClaimInfo()?.providerSlug!,
        solicitudReclamoId,
        this.claimService.initialClaimInfo()?.token!
      )
      .subscribe(
        (response) => {
          if (response.statusCode === 202) {
            setTimeout(() => {
              this.loadClaimInfo(solicitudReclamoId);
            }, environment.pollingInterval);
          } else {
            this.blockClaimCreationProgressModalDismiss = false;

            this.modalService.dismiss(
              this.modalRefs.biometric.claimCreationProgress
            );

            this.toastService.show({
              ref: 'claimCreated',
              color: 'success',
              message: { key: 'app.pageId.claim-generated' },
              icon: bootstrapShieldFillCheck,
            });

            this.claimService.setClaim(response.data);

            this.router.navigate(
              [
                'app',
                this.claimService.initialClaimInfo()?.claimInfo.type,
                'consult-claim',
                response.data.id,
              ],
              { replaceUrl: true }
            );

            if (response.data.urlRedirect) {
              this.openRedirectConfirmationModal(response.data.urlRedirect);
            }
          }
        },
        (err: ResponseError) => {
          this.blockClaimCreationProgressModalDismiss = false;

          this.modalService.dismiss(
            this.modalRefs.biometric.claimCreationProgress
          );

          this.toastService.show({
            ref: 'claimCreated',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: { message: err.error.message, code: err.error.error },
            },
            icon: bootstrapXCircle,
          });
        }
      );
  }

  loadPreAuthorizedClaimInfo(solicitudReclamoId: number) {
    this.claimService
      .getCreatedClaimInfo(
        this.claimService.initialClaimInfo()?.providerSlug!,
        solicitudReclamoId,
        this.claimService.initialClaimInfo()?.token!
      )
      .subscribe(
        (response) => {
          if (response.statusCode === 202) {
            setTimeout(() => {
              this.loadPreAuthorizedClaimInfo(solicitudReclamoId);
            }, environment.pollingInterval);
          } else {
            this.blockClaimCreationProgressModalDismiss = false;

            this.modalService.dismiss(
              this.modalRefs.biometric.preauthorizationCreationProgress
            );

            sessionStorage.removeItem('initialClaimInfo');

            this.claimService.setClaim(response.data);

            this.openPreauthorizedClaimCreatedModal();
          }
        },
        (err: ResponseError) => {
          this.blockClaimCreationProgressModalDismiss = false;

          this.modalService.dismiss(
            this.modalRefs.biometric.preauthorizationCreationProgress
          );

          this.toastService.show({
            ref: 'preauthorizationCreatedError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: { message: err.error.message, code: err.error.error },
            },
            icon: bootstrapXCircle,
          });
        }
      );
  }

  openRedirectConfirmationModal(urlRedirect: string) {
    const modalConfig = () => ({
      type: 'confirmation',
      title: 'app.pageId.redirect.title',
      description: 'app.pageId.redirect.description',
      size: 'sm',
      actions: {
        cancel: {
          type: 'button',
          label: {
            key: 'app.pageId.redirect.cancel',
          },
          color: 'tertiary',
          theme: 'light',
          action: () => {
            this.modalService.dismiss('redirect');
          },
        },
        confirm: {
          type: 'button',
          label: {
            key: 'app.pageId.redirect.confirm',
          },
          color: 'primary',
          action: () => {
            let a = document.createElement('a') as HTMLAnchorElement;
            a.href = urlRedirect;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.removeChild(a);
            this.modalService.dismiss('redirect');
          },
        },
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      'redirect'
    );
  }
}
